// Browser-side client for /api (types and productsSearch are shared with the
// server-side lib/catalog-server.ts). Relative paths — the site and /api share one
// origin by design (see docs/architecture.md#routing), so the browser attaches
// the session cookie automatically and no CORS/base-URL config is needed here.
import { trackRead } from "./connection";

//
// No answer (network down, timed out) comes back as status 0, never a throw.
// Reads (GET) on a weak network (F203): each try gives up after
// READ_TIMEOUT_MS, and status 0 or a gateway error is tried again after each
// of RETRY_DELAYS_MS; lib/connection.ts tracks slow and failed reads for /app.
// Writes are sent once, with no time limit: one that timed out here might
// still have gone through (a payment, a withdrawal), so it's never repeated.
const READ_TIMEOUT_MS = 15_000;
const RETRY_DELAYS_MS = [2_000, 5_000, 10_000];
const retryable = (status: number) => status === 0 || status === 502 || status === 503 || status === 504;

async function send<T>(path: string, init: RequestInit | undefined, timeoutMs?: number): Promise<{ status: number; data: T | null }> {
  const controller = timeoutMs ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
  try {
    const res = await fetch(path, {
      ...init,
      ...(controller ? { signal: controller.signal } : {}),
      credentials: "include",
      headers: { "Content-Type": "application/json", ...init?.headers },
    });
    const data = res.status === 204 ? null : ((await res.json().catch(() => null)) as T | null);
    return { status: res.status, data };
  } catch {
    return { status: 0, data: null };
  } finally {
    clearTimeout(timer);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<{ status: number; data: T | null }> {
  const read = !init?.method || init.method.toUpperCase() === "GET";
  if (!read) return send<T>(path, init);
  return trackRead(
    async (onRetry) => {
      for (let attempt = 0; ; attempt++) {
        const result = await send<T>(path, init, READ_TIMEOUT_MS);
        if (!retryable(result.status) || attempt >= RETRY_DELAYS_MS.length) return result;
        onRetry();
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
      }
    },
    // Gave up: no answer, or the server failed (a 500 isn't retried, but its page can't load either).
    (result) => result.status === 0 || result.status >= 500,
  );
}

export interface Me {
  id: string;
  phone: string;
  email: string | null;
  emailVerifiedAt: string | null;
  verificationStatus: "unverified" | "pending" | "verified" | "rejected";
  twoFactorEnabled?: boolean;
  /** What the user chose to be called; null until set. */
  displayName?: string | null;
  avatarUrl?: string | null;
  /** From identity verification; read-only. */
  legalName?: string | null;
  /** Verification level one (Shahkar) passed. */
  levelOnePassed?: boolean;
  /** «پیامک‌های تبلیغاتی» (2.30): campaign SMS on. */
  marketingSms?: boolean;
  /** The installed app has run on an iPhone for this account (F173). */
  iosAppSeen?: boolean;
}

/** Sent with a sign-in request once the server has asked for a CAPTCHA (F113). */
export interface CaptchaAnswer {
  captchaId?: string;
  captchaAnswer?: string;
}

/** What a successful first factor returns: signed in, or a second step to do (2FA). */
export type SignInResult =
  | { ok: true; isNewUser: boolean; user: Me; mfaRequired?: undefined }
  | { ok: true; mfaRequired: true; mfaToken: string }
  | { error: string };

export function getCaptcha() {
  return request<{ captchaId: string; svg: string }>("/api/auth/captcha");
}

export function verifyMfa(mfaToken: string, code: string) {
  return request<SignInResult>("/api/auth/mfa", { method: "POST", body: JSON.stringify({ mfaToken, code }) });
}

export function getMe() {
  return request<{ user: Me }>("/api/auth/me");
}

export type OtpRequestResponse =
  | { ok: true; phone: string; resendInSeconds: number; codeLength: number }
  | { error: "invalid_phone" | "cooldown" | "rate_limited" | "sms_failed"; retryAfterSeconds?: number };

export function requestOtp(phone: string, captcha: CaptchaAnswer = {}) {
  return request<OtpRequestResponse | { error: "captcha_required" | "captcha_invalid" }>("/api/auth/otp/request", {
    method: "POST",
    body: JSON.stringify({ phone, ...captcha }),
  });
}

export function verifyOtp(phone: string, code: string, captcha: CaptchaAnswer = {}) {
  return request<SignInResult>("/api/auth/otp/verify", {
    method: "POST",
    body: JSON.stringify({ phone, code, ...captcha }),
  });
}

export function loginWithEmail(email: string, password: string, captcha: CaptchaAnswer = {}) {
  return request<SignInResult>("/api/auth/login/email", {
    method: "POST",
    body: JSON.stringify({ email, password, ...captcha }),
  });
}

export function logout() {
  return request<{ ok: true }>("/api/auth/logout", { method: "POST" });
}

// ── Push notifications (F174) ───────────────────────────────────────────

export type NotificationCategory = "orders" | "support" | "wallet" | "promotions";

export interface PushDevice {
  id: string;
  endpoint: string;
  platform: string;
  deviceLabel: string | null;
  createdAt: string;
}

/** Whether the site may offer notifications now (keys present, admin's master switch on). */
export function getPushConfig() {
  return request<{ available: boolean; publicKey: string | null }>("/api/notifications/config");
}

export function getNotificationPreferences() {
  return request<{ available: boolean; categories: { category: NotificationCategory; enabled: boolean }[]; devices: PushDevice[] }>("/api/notifications/preferences");
}

export function setNotificationPreference(category: NotificationCategory, enabled: boolean) {
  return request<{ categories: { category: NotificationCategory; enabled: boolean }[] }>("/api/notifications/preferences", {
    method: "PUT",
    body: JSON.stringify({ category, enabled }),
  });
}

export function savePushSubscription(subscription: PushSubscriptionJSON, promotions?: boolean) {
  return request<{ id: string }>("/api/notifications/subscriptions", {
    method: "POST",
    body: JSON.stringify({ endpoint: subscription.endpoint, keys: subscription.keys, promotions }),
  });
}

export function removePushDevice(target: { id: string } | { endpoint: string }) {
  return request<{ ok: true }>("/api/notifications/subscriptions/remove", { method: "POST", body: JSON.stringify(target) });
}

/** The installed app tells the account it runs on an iPhone (F173; once per device). */
export function markIosAppSeen() {
  return request<{ ok: true }>("/api/auth/me/ios-app", { method: "POST" });
}

// ── Verification steps (F114) ───────────────────────────────────────────

export type VerificationKind = "shahkar" | "identity";

export interface VerificationStep {
  kind: VerificationKind;
  titleFa: string;
  descriptionFa: string;
  required: boolean;
  status: "pending" | "passed" | "failed" | "not_required";
  requiredReason: string | null;
  passedAt: string | null;
  result: Record<string, string> | null;
}

export interface Verifications {
  verificationStatus: Me["verificationStatus"];
  identity: { phone: string; nationalIdMasked: string | null; legalName: string | null };
  steps: VerificationStep[];
}

export function getVerifications() {
  return request<Verifications>("/api/account/verifications");
}

export function submitShahkar(nationalId: string) {
  return request<{ ok: true; verified: boolean } | { error: string }>("/api/account/verifications/shahkar", { method: "POST", body: JSON.stringify({ nationalId }) });
}

export function submitIdentity(birthDate: string, nationalId: string) {
  return request<{ ok: true; verified: boolean } | { error: string }>("/api/account/verifications/identity", { method: "POST", body: JSON.stringify({ birthDate, nationalId }) });
}

// ── Bank details (F115) ──────────────────────────────────────────────────

export interface BankCard {
  id: string;
  masked: string;
  bank: { code: string; name: string | null } | null;
  status: "pending" | "verified" | "rejected";
  verifiedAt: string | null;
}

export interface Sheba {
  id: string;
  iban: string;
  bank: { code: string; name: string | null } | null;
  holderName: string | null;
  /** Checked in the background after adding: pending → verified / rejected, or failed (provider didn't answer). */
  status: "pending" | "verified" | "rejected" | "failed";
  verified: boolean;
}

export const getCards = () => request<{ cards: BankCard[]; canAdd: boolean }>("/api/account/cards");
/** The card inquiry checks the card against the national ID (F148, F155: card number only). */
export const addCard = (cardNumber: string) =>
  request<{ card: BankCard } | { error: string }>("/api/account/cards", { method: "POST", body: JSON.stringify({ cardNumber }) });
export const removeCard = (id: string) => request<{ ok: true }>(`/api/account/cards/${id}`, { method: "DELETE" });
export const getShebas = () => request<{ shebas: Sheba[] }>("/api/account/shebas");
export const addSheba = (iban: string) => request<{ sheba: Sheba } | { error: string }>("/api/account/shebas", { method: "POST", body: JSON.stringify({ iban }) });
export const removeSheba = (id: string) => request<{ ok: true }>(`/api/account/shebas/${id}`, { method: "DELETE" });
export const recheckSheba = (id: string) => request<{ sheba: Sheba } | { error: string }>(`/api/account/shebas/${id}/recheck`, { method: "POST" });

// ── Companies «شخص حقوقی» (F218): staff review each of the 5 fields ─────────

export type CompanyField = "name" | "companyId" | "postalCode" | "gazette" | "letter";
export type FieldStatus = "pending" | "approved" | "rejected";
export interface CompanyFile {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}
export interface Company {
  id: string;
  status: "pending" | "needs_fix" | "approved" | "rejected";
  companyName: string;
  nationalCompanyId: string;
  postalCode: string;
  gazette: CompanyFile | null;
  letter: CompanyFile | null;
  fields: Record<CompanyField, FieldStatus>;
  /** The staff's message from the last review. */
  staffMessage: string | null;
  submittedAt: string;
  reviewedAt: string | null;
}
export interface CompanyInput {
  companyName: string;
  nationalCompanyId: string;
  postalCode: string;
  gazetteFileId: string;
  letterFileId: string;
}

export const getCompanies = () => request<{ companies: Company[]; approvedLimit: number }>("/api/account/companies");
export const addCompany = (input: CompanyInput) => request<{ company: Company } | ApiError>("/api/account/companies", { method: "POST", body: JSON.stringify(input) });
/** After «نیاز به اصلاح»: only the rejected fields. */
export const fixCompany = (id: string, input: Partial<CompanyInput>) =>
  request<{ company: Company } | ApiError>(`/api/account/companies/${id}`, { method: "PATCH", body: JSON.stringify(input) });
export const removeCompany = (id: string) => request<{ ok: true }>(`/api/account/companies/${id}`, { method: "DELETE" });
export const companyFileUrl = (fileId: string) => `/api/account/companies/files/${fileId}`;

/** Uploads one paper (JPG/PNG/PDF, at most 2 MB); its id goes with the form. */
export async function uploadCompanyFile(kind: "gazette" | "letter", file: File): Promise<{ status: number; data: { file: CompanyFile } | ApiError | null }> {
  try {
    const res = await fetch(`/api/account/companies/files?kind=${kind}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream", "X-Filename": encodeURIComponent(file.name) },
      body: file,
    });
    return { status: res.status, data: (await res.json().catch(() => null)) as { file: CompanyFile } | ApiError | null };
  } catch {
    return { status: 0, data: null };
  }
}

export interface SessionInfo {
  id: string;
  device: { browser: string | null; os: string | null; kind: "mobile" | "tablet" | "desktop"; label: string };
  deviceLabel: string | null;
  ipAddress: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  current: boolean;
}

export function revokeOtherSessions() {
  return request<{ ok: true; revoked: number }>("/api/auth/sessions/revoke-others", { method: "POST" });
}

// ── Two-factor sign-in (F113) ────────────────────────────────────────────

export function getTwoFactor() {
  return request<{ enabled: boolean; enabledAt: string | null; recoveryCodesLeft: number }>("/api/account/2fa");
}

export function startTwoFactorSetup() {
  return request<{ secret: string; otpauthUrl: string } | { error: string }>("/api/account/2fa/setup", { method: "POST" });
}

export function enableTwoFactor(code: string) {
  return request<{ ok: true; recoveryCodes: string[] } | { error: string }>("/api/account/2fa/enable", { method: "POST", body: JSON.stringify({ code }) });
}

export function newRecoveryCodes(code: string) {
  return request<{ ok: true; recoveryCodes: string[] } | { error: string }>("/api/account/2fa/recovery-codes", { method: "POST", body: JSON.stringify({ code }) });
}

export function disableTwoFactor(code: string) {
  return request<{ ok: true } | { error: string }>("/api/account/2fa/disable", { method: "POST", body: JSON.stringify({ code }) });
}

export function getSessions() {
  return request<{ sessions: SessionInfo[] }>("/api/auth/sessions");
}

export function revokeSession(id: string) {
  return request<{ ok: true }>(`/api/auth/sessions/${id}`, { method: "DELETE" });
}

export function setAccountEmail(email: string) {
  return request<{ ok: true } | { error: string }>("/api/account/email", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function verifyAccountEmail(code: string) {
  return request<{ ok: true } | { error: string }>("/api/account/email/verify", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export function setAccountPassword(password: string) {
  return request<{ ok: true } | { error: string }>("/api/account/password", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export type Metal = "gold" | "silver";

/** The home chart ranges (F142): today and the week hourly, the month daily (the default). */
export type PriceRange = "day" | "week" | "month";

export interface PriceChartResponse {
  range: PriceRange;
  series: { metal: Metal; points: { at: string; closeRialPerGram: string | null }[] }[];
}

/** One metal on the home price card. */
export interface PriceSeries {
  metal: Metal;
  title: string;
  /** The unit after the price («…/گرم»). */
  unitLabel: string;
  /** Toman per gram, oldest first, the current hour or day last. Gaps carry the previous close. */
  values: number[];
  /** Tooltip label per value, Tehran time: «۱۴:۰۰» (day), «سه‌شنبه ۱۴:۰۰» (week), «۲۴ مهر» (month). */
  labels: string[];
}

const TEHRAN = "Asia/Tehran";
const hourMinute = new Intl.DateTimeFormat("fa-IR", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: TEHRAN });
const weekdayName = new Intl.DateTimeFormat("fa-IR", { weekday: "long", timeZone: TEHRAN });
const dayMonth = new Intl.DateTimeFormat("fa-IR-u-ca-persian", { day: "numeric", month: "long", timeZone: TEHRAN });

function pointLabel(range: PriceRange, at: Date): string {
  if (range === "day") return hourMinute.format(at);
  if (range === "week") return `${weekdayName.format(at)} ${hourMinute.format(at)}`;
  return dayMonth.format(at);
}

/** api's chart response as the card draws it; null (api down) gives empty series. */
export function toPriceSeries(data: PriceChartResponse | null): PriceSeries[] {
  return (["gold", "silver"] as const).map((metal) => {
    const points = data?.series.find((s) => s.metal === metal)?.points ?? [];
    const values: number[] = [];
    const labels: string[] = [];
    let last: number | null = null;
    for (const point of points) {
      if (point.closeRialPerGram !== null) last = Math.round(Number(point.closeRialPerGram) / 10);
      if (last === null) continue; // no history yet before the first known point
      values.push(last);
      labels.push(pointLabel(data!.range, new Date(point.at)));
    }
    return { metal, title: metal === "gold" ? "طلا" : "نقره", unitLabel: "گرم", values, labels };
  });
}

export function getPriceChart(range: PriceRange) {
  return request<PriceChartResponse>(`/api/prices/chart?range=${range}`);
}

export interface ProductPrice {
  /** Rial, integer string; null when the price is halted (F17): «قیمت در دسترس نیست». */
  priceRial: string | null;
  metalValueRial: string | null;
  commissionRial: string | null;
  halted: boolean;
}

export interface CatalogProduct {
  id: string;
  slug: string;
  title: string;
  metal: Metal;
  brand: { slug: string; name: string };
  weight: { slug: string; label: string; grams: string };
  fineness: { slug: string; label: string; fineness: string; karat: number | null };
  packaging: { slug: string; name: string };
  images: { url: string; alt: string }[];
  availableQuantity: number;
  price: ProductPrice;
  /** A live festival's gift with this bar (2.29). */
  gift?: string | null;
}

export interface ProductsPage {
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  pricesUpdatedAt: string | null;
  products: CatalogProduct[];
}

export type StoreSort = "default" | "price_asc" | "price_desc" | "weight_asc" | "weight_desc";

export interface ProductsQuery {
  metal?: Metal;
  weight?: string;
  fineness?: string;
  brand?: string;
  sort?: StoreSort;
  /** Only bars that can be bought now (quick-buy, F46). */
  inStock?: boolean;
  page?: number;
  pageSize?: number;
}

export function productsSearch(query: ProductsQuery) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === "" || value === false || (key === "sort" && value === "default")) continue;
    params.set(key, value === true ? "1" : String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** A bar with ShemShop's buyback estimate for one (F80, F125); null while buy prices are halted. */
export interface BuybackEstimate extends CatalogProduct {
  buybackRial: string | null;
}

/** /api/buyback/estimates: nothing but `enabled: false` while buyback is switched off (F126). */
export interface BuybackEstimates {
  enabled: boolean;
  pricesUpdatedAt?: string | null;
  products: BuybackEstimate[];
}

export function getProducts(query: ProductsQuery = {}) {
  return request<ProductsPage>(`/api/catalog/products${productsSearch(query)}`);
}

export interface Facets {
  metals: { slug: Metal; count: number }[];
  weights: { metal: Metal; slug: string; label: string; count: number }[];
  finenesses: { metal: Metal; slug: string; label: string; karat: number | null; count: number }[];
  brands: { slug: string; name: string; count: number }[];
  /** With inStock only: each metal × fineness × weight that can be bought now (quick-buy, 2.5). */
  combos?: BuyCombo[];
}

export interface BuyCombo {
  metal: Metal;
  fineness: { slug: string; label: string; karat: number | null };
  weight: { slug: string; label: string; grams: string };
  count: number;
  /** The cheapest matching bar, Rial; null when prices are halted. */
  minPriceRial: string | null;
}

/** A brand's profile on /store/brand/<slug> (2.13). */
export interface BrandProfile {
  slug: string;
  name: string;
  nameEn: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  serialCheckUrl: string | null;
  metals: { metal: Metal; count: number; inStock: number; lightest: string | null; heaviest: string | null; finenesses: string[] }[];
}

export interface CurrentPrices {
  updatedAt: string | null;
  halted: boolean;
  prices: Record<string, string | null>;
}

export function getCurrentPrices() {
  return request<CurrentPrices>("/api/prices/current");
}

/** The category a product is priced from (for "how the price is calculated"). */
export interface PriceBasis {
  metal: Metal;
  referenceFineness: string;
  sellRialPerGram: string | null;
  status: "fresh" | "fallback" | "halted";
}

export interface ProductDetail extends CatalogProduct {
  description: string | null;
  specs: Record<string, string>;
  commissionPercent: string;
  dimensionsMm: { length: string; width: string; thickness: string } | null;
  serialCheckUrl: string | null;
  brandWebsite: string | null;
  updatedAt: string;
}

export interface ProductPage {
  product: ProductDetail;
  basis: PriceBasis | null;
  pricesUpdatedAt: string | null;
  related: CatalogProduct[];
}

export interface LiveProductPrice {
  updatedAt: string | null;
  price: ProductPrice;
  basis: PriceBasis | null;
}

export function getProductPrice(id: string) {
  return request<LiveProductPrice>(`/api/prices/product/${id}`);
}

// ── Cart (plan 2.6) ─────────────────────────────────────────────────────

export type CartItemStatus = "ok" | "unavailable" | "insufficient" | "halted";

export interface CartItem {
  productId: string;
  quantity: number;
  status: CartItemStatus;
  /** Null when the product is no longer sold (hidden or archived). */
  product: CatalogProduct | null;
  lineTotalRial: string | null;
}

export interface Cart {
  items: CartItem[];
  count: number;
  rows: {
    itemsRial: string;
    /** The applied coupon's row (2.28); amount positive, subtracted. */
    discount: { code: string; labelFa: string; amountRial: string } | null;
    /** Enabled charge rows (2.8); amount null with a note when not added yet. */
    charges: { code: string; labelFa: string; amountRial: string | null; note: "at_checkout" | "postpaid" | null }[];
    taxRial: string | null;
    totalRial: string;
  };
  halted: boolean;
  quoteId: string | null;
  refreshSeconds: number;
  /** The worker's next price fetch (F157); count down to it on the server's clock. */
  nextRefreshAt: string;
  serverNow: string;
  payment: { gatewayMaxRial: string; walletRial: string | null; capacityRial: string; exceedsCapacity: boolean };
  /** The festival gift this cart gets (2.29); it doesn't change any amount. */
  gift: CartGift | null;
  /** The code on the cart; when no longer valid, why (and the cart can't go on until it's removed). */
  coupon: { code: string; ok: true } | { code: string; ok: false; error: CouponError; detail: { minRial?: string } | null } | null;
  canCheckout: boolean;
}

export interface CartGift {
  festival: string;
  slug: string;
  name: string;
  imageUrl: string | null;
}

/** The live festival's home card (F213). */
export interface FestivalCardData {
  slug: string;
  title: string;
  text: string;
  ctaLabel: string;
  ctaHref: string;
  imageUrl: string | null;
  tone: "yellow" | "violet" | "teal" | "blue" | "orange" | "pink";
  endsAt: string;
  remaining: number | null;
}

/** A festival's page (2.29): its terms, gift and the bars that come with it. */
export interface FestivalPage {
  festival: {
    name: string;
    slug: string;
    terms: string | null;
    startsAt: string;
    endsAt: string;
    state: "scheduled" | "live" | "ended";
    minOrderRial: string | null;
    available: boolean;
    gift: { name: string; imageUrl: string | null };
  };
  products: CatalogProduct[];
}

export type GiftStatus = "pending" | "reserved" | "in_transfer" | "ready" | "handed_over";

export type CouponError =
  | "coupon_not_found"
  | "coupon_not_started"
  | "coupon_expired"
  | "coupon_used_up"
  | "coupon_already_used"
  | "coupon_first_order_only"
  | "coupon_min_order"
  | "coupon_not_applicable";

/** Fired on window whenever the cart changes, so the header badge follows. */
export const CART_CHANGED_EVENT = "shemshop:cart-changed";

function announce(result: { status: number; data: Cart | null }) {
  if (result.data && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CART_CHANGED_EVENT, { detail: { count: result.data.count } }));
  }
  return result;
}

export function getCart() {
  return request<Cart>("/api/cart").then(announce);
}

export function getCartCount() {
  return request<{ count: number }>("/api/cart/count");
}

export function addToCart(productId: string, quantity = 1) {
  return request<Cart>("/api/cart/items", { method: "POST", body: JSON.stringify({ productId, quantity }) }).then(announce);
}

export function setCartQuantity(productId: string, quantity: number) {
  return request<Cart>(`/api/cart/items/${productId}`, { method: "PATCH", body: JSON.stringify({ quantity }) }).then(announce);
}

export function removeFromCart(productId: string) {
  return request<Cart>(`/api/cart/items/${productId}`, { method: "DELETE" }).then(announce);
}

/** 200: the cart with the code applied; 422 `{ error: CouponError, detail }`; 401 signed out; 429 too many wrong codes. */
export function applyCoupon(code: string) {
  return request<Cart | { error: CouponError | "sign_in_required" | "rate_limited"; detail: { minRial?: string } | null }>("/api/cart/coupon", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export function setMarketingSms(enabled: boolean) {
  return request<{ ok: true; enabled: boolean }>("/api/account/marketing-sms", { method: "PUT", body: JSON.stringify({ enabled }) });
}

export function removeCoupon() {
  return request<Cart>("/api/cart/coupon", { method: "DELETE" });
}

// ── Checkout (plan 2.7, 2.8; F110–F112) ─────────────────────────────────

export interface Address {
  id: string;
  postalCode: string;
  address: string;
  receiverName: string | null;
  receiverPhone: string | null;
  province: string | null;
  city: string | null;
  isDefault: boolean;
}

export interface CheckoutContext {
  user: {
    phone: string;
    verificationStatus: Me["verificationStatus"];
    name: string | null;
    displayName: string | null;
    avatarUrl: string | null;
    addresses: Address[];
    maxAddresses: number;
    walletBalanceRial: string;
    /** Verified cards the remainder can be paid with (F154). */
    cards: PayingCard[];
    pendingOrderId: string | null;
  } | null;
  /** shipping | courier | pickup; `postalPrefixes` limits the addresses a method takes (courier: Tehran, F224). */
  deliveryMethods: { code: string; labelFa: string; descriptionFa: string | null; fulfillment: "shipping" | "pickup"; postalPrefixes: string[] | null }[];
  pickupWarehouses: { id: string; name: string; address: string | null; instructions: string | null }[];
  gatewayMaxRial: string;
  maxShippableRial: string | null;
  /** «پرداخت با کارت» (direct gateway) and «پرداخت اعتباری» (BNPL): disabled when no provider is selected in admin. */
  payment: { direct: GatewayOption; bnpl: GatewayOption };
}

export interface GatewayOption {
  available: boolean;
  nameFa: string | null;
  /** The test bank: no money moves. */
  simulated: boolean;
}

export interface PayingCard {
  id: string;
  masked: string;
  bank: { code: string; name: string | null } | null;
}

/** A gateway payment's result page (/payment/<id>): `processing` until api's payment processor decides. */
export interface PaymentStatus {
  id: string;
  purpose: "order_payment" | "wallet_top_up";
  orderId: string | null;
  status: "processing" | "paid" | "failed" | "expired";
  waitingForBank: boolean;
  reason: string | null;
  bankCode: string | null;
  bankMessage: string | null;
  amountRial: string;
  cardMasked: string | null;
  expectedCardMasked: string | null;
  reference: string | null;
  simulated: boolean;
  sessionExpiresAt: string;
}

export const getPaymentStatus = (id: string) => request<PaymentStatus | ApiError>(`/api/payments/${id}`);

/** The address for a postal code (F155), to fill the address form. */
export interface PostalAddress {
  postalCode: string;
  province: string | null;
  city: string | null;
  address: string;
}
export const lookupPostalCode = (postalCode: string) =>
  request<{ address: PostalAddress } | ApiError>("/api/account/postal-lookup", { method: "POST", body: JSON.stringify({ postalCode }) });

export type OrderStatus = "pending_payment" | "paid" | "processing" | "shipped" | "delivered" | "ready_for_pickup" | "picked_up" | "cancelled" | "refunded";

export interface OrderView {
  id: string;
  number: number;
  status: OrderStatus;
  fulfillmentMethod: "shipping" | "pickup";
  /** The method picked in step 1 (shipping, courier, pickup). */
  deliveryMethod: string;
  shippingAddress: Omit<Address, "id" | "isDefault"> | null;
  pickupWarehouse: { id: string; name: string; address: string | null; instructions: string | null } | null;
  pricedAt: string;
  quoteExpiresAt: string;
  serverNow: string;
  items: {
    productId: string;
    title: string;
    slug: string;
    quantity: number;
    unitPriceRial: string;
    metalValueRial: string;
    commissionRial: string;
    commissionPercent: string;
    lineTotalRial: string;
    /** Serials of the bars handed over (F6), once fulfilled. */
    units: { serialNumber: string; certificateNumber: string | null }[];
  }[];
  rows: { itemsRial: string; adjustments: { kind: string; code: string; labelFa: string; amountRial: string }[]; totalRial: string };
  /** The festival gift (2.29), at 0 on the invoice (F212). */
  gift: { name: string; festival: string; imageUrl: string | null; status: GiftStatus } | null;
  postPaymentMode: "prepaid" | "postpaid" | null;
  trackingNumber: string | null;
  shippingCarrier: string | null;
  /** The one-time pickup code (F27), only while the order is ready_for_pickup. */
  pickupCode: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  readyAt: string | null;
  pickedUpAt: string | null;
  payments: { id: string; method: string; amountRial: string; walletPartRial: string; status: string; failureReason: string | null; cardMasked: string | null; reference: string | null; at: string }[];
  walletBalanceRial: string;
  gatewayMaxRial: string;
  paidAt: string | null;
  createdAt: string;
}

export interface ApiError {
  error: string;
  detail?: unknown;
}

export function getCheckoutContext() {
  return request<CheckoutContext>("/api/checkout/context");
}

/** The user's saved addresses (F158: at most `max`, kept as saved; changes go through support). */
export function getAddresses() {
  return request<{ addresses: Address[]; max: number }>("/api/account/addresses");
}

export function addAddress(input: { postalCode: string; address: string; receiverName?: string | null; receiverPhone?: string | null }) {
  return request<{ address: Address } | ApiError>("/api/account/addresses", { method: "POST", body: JSON.stringify(input) });
}

export function createOrder(delivery: { method: string; addressId?: string; warehouseId?: string }) {
  return request<{ order: OrderView } | ApiError>("/api/checkout/order", { method: "POST", body: JSON.stringify(delivery) });
}

export function getCheckoutOrder(id: string) {
  return request<{ order: OrderView } | ApiError>(`/api/checkout/order/${id}`);
}

export function payOrder(id: string, input: { useWallet: boolean; expectedTotalRial: string; bankCardId: string | null }) {
  return request<{ kind: "paid"; orderId: string } | { kind: "redirect"; url: string } | ApiError>(`/api/checkout/order/${id}/pay`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function getOrder(id: string) {
  return request<{ order: OrderView } | ApiError>(`/api/orders/${id}`);
}

/** A row of the user's orders; `finishedAt` is when it was delivered or picked up. */
export type OrderListRow = {
  id: string;
  number: number;
  status: OrderStatus;
  totalRial: string;
  fulfillment: "shipping" | "pickup";
  createdAt: string;
  paidAt: string | null;
  finishedAt: string | null;
};

export function getOrders() {
  return request<{ orders: OrderListRow[] }>("/api/orders");
}

// ── Wallet (plan 2.7, 2.12) ───────────────────────────────────────────────

export interface WalletView {
  balanceRial: string;
  heldRial: string;
  limits: { minTopUpRial: string; minWithdrawalRial: string; dailyMaxWithdrawalRial: string };
  topUpAvailable: boolean;
  /** The direct gateway is the test bank (F154). */
  gatewaySimulated: boolean;
  /** Verified cards a top-up can be paid with. */
  cards: PayingCard[];
  gatewayMaxRial: string | null;
  bankAccounts: { id: string; iban: string; holderName: string | null }[];
  topUps: { id: string; amountRial: string; status: "started" | "verified" | "failed" | "expired"; failureReason: string | null; createdAt: string; reference: string | null }[];
  withdrawals: { id: string; amountRial: string; status: "requested" | "approved" | "paid" | "rejected" | "cancelled"; iban: string; bankReference: string | null; note: string | null; createdAt: string; paidAt: string | null }[];
  movements: { id: string; type: string; referenceType: string | null; referenceId: string | null; createdAt: string; amountRial: string }[];
}

export function getWallet() {
  return request<WalletView>("/api/wallet");
}

export function startTopUp(amountRial: string, bankCardId: string) {
  return request<{ url: string } | ApiError>("/api/wallet/top-ups", { method: "POST", body: JSON.stringify({ amountRial, bankCardId }) });
}

export function requestWithdrawal(bankAccountId: string, amountRial: string) {
  return request<{ ok: true; id: string } | ApiError>("/api/wallet/withdrawals", { method: "POST", body: JSON.stringify({ bankAccountId, amountRial }) });
}

export function cancelWithdrawal(id: string) {
  return request<{ ok: true } | ApiError>(`/api/wallet/withdrawals/${id}/cancel`, { method: "POST" });
}

// ── Profile: display name and image ───────────────────────────────────────

export function setDisplayName(displayName: string | null) {
  return request<{ ok: true; displayName: string | null } | ApiError>("/api/account/profile", { method: "PATCH", body: JSON.stringify({ displayName }) });
}

/** The image file as the raw body (the api resizes it to a 256 px square). */
export async function uploadAvatar(file: File) {
  const res = await fetch("/api/account/avatar", { method: "POST", credentials: "include", headers: { "Content-Type": file.type }, body: file });
  const data = (await res.json().catch(() => null)) as ({ ok: true; avatarUrl: string } | ApiError) | null;
  return { status: res.status, data };
}

export function deleteAvatar() {
  return request<{ ok: true } | ApiError>("/api/account/avatar", { method: "DELETE" });
}

// ── Tickets (F147, plan 2.24) ─────────────────────────────────────────────

export type TicketReason = "account" | "order" | "wallet" | "buyback" | "other";
/** The customer's view of a ticket's status. */
export type TicketStatus = "submitted" | "in_progress" | "answered" | "closed";

export interface TicketRow {
  number: number;
  subject: string;
  reason: TicketReason;
  status: TicketStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TicketFile {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}
export interface AttachmentLimits {
  maxFiles: number;
  maxFileMb: number;
}

/** Oldest first, like a chat: messages both ways, with log lines (opened, referred, closed, reopened). */
export type TicketEvent =
  | { id: string; type: "created" | "closed" | "reopened" | "merged"; at: string }
  | { id: string; type: "referred"; at: string; department: "support" | "finance" | "warehouse" | "commerce" }
  | { id: string; type: "message" | "reply"; at: string; format: "text" | "html"; body: string; attachments: (TicketFile & { url: string })[] };

export interface TicketDetail {
  ticket: TicketRow & { orderNumber: number | null; mergedIntoNumber: number | null; canReply: boolean };
  timeline: TicketEvent[];
  attachmentLimits: AttachmentLimits;
}

export function getTickets() {
  return request<{ tickets: TicketRow[]; attachmentLimits: AttachmentLimits }>("/api/tickets");
}

/** Uploads one file for a ticket message; its id goes with the message. */
export async function uploadTicketFile(file: File): Promise<{ status: number; data: TicketFile | ApiError | null }> {
  const res = await fetch("/api/tickets/attachments", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": file.type, "X-Filename": encodeURIComponent(file.name) },
    body: file,
  });
  return { status: res.status, data: (await res.json().catch(() => null)) as TicketFile | ApiError | null };
}

export function createTicket(input: { reason: TicketReason; subject: string; body: string; orderId?: string | null; attachmentIds?: string[] }) {
  return request<{ ok: true; number: number } | ApiError>("/api/tickets", { method: "POST", body: JSON.stringify(input) });
}

export function getTicket(number: string) {
  return request<TicketDetail | ApiError>(`/api/tickets/${number}`);
}

export function replyToTicket(number: string, body: string, attachmentIds: string[] = []) {
  return request<{ ok: true } | ApiError>(`/api/tickets/${number}/messages`, { method: "POST", body: JSON.stringify({ body, attachmentIds }) });
}

// ── Invoices «صدور صورتحساب» (F219) ──────────────────────────────────────

export interface OrderInvoice {
  id: string;
  number: number;
  issuedAt: string;
  buyerType: "person" | "company";
  buyerName: string;
}

export interface InvoiceOptions {
  person: { verified: boolean; name: string | null; nationalIdMasked: string | null } | null;
  companies: { id: string; name: string; nationalId: string; postalCode: string }[];
}

export function getOrderInvoice(orderId: string) {
  return request<{ invoice: OrderInvoice | null; available: boolean } | ApiError>(`/api/orders/${orderId}/invoice`);
}

export function getInvoiceOptions(orderId: string) {
  return request<InvoiceOptions | ApiError>(`/api/orders/${orderId}/invoice/options`);
}

export function issueInvoice(orderId: string, body: { type: "person" | "company"; companyId?: string }) {
  return request<{ invoice: OrderInvoice } | ApiError>(`/api/orders/${orderId}/invoice`, { method: "POST", body: JSON.stringify(body) });
}

/** The PDF, through api (only the buyer's own). */
export const invoicePdfUrl = (invoiceId: string) => `/api/invoices/${invoiceId}/pdf`;

/** The same page as an image, for «نمایش صورتحساب». */
export const invoicePreviewUrl = (invoiceId: string) => `/api/invoices/${invoiceId}/preview`;
